import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, Database, Mail, FileText, Crown } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';

const PrivacyPolicy = () => {
  const { settings } = useSiteSettings();
  
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero */}
      <div className="bg-[#0A0A0A] text-white py-20 border-b border-[#D4AF37]/20">
        <div className="container mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#D4AF37]/50">
            <Shield className="h-10 w-10 text-[#D4AF37]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 font-heading">Privacy Policy</h1>
          <p className="text-xl text-[#888888] font-body max-w-2xl mx-auto">
            Your privacy matters. Learn how we protect and handle your information.
          </p>
          <p className="text-sm text-[#D4AF37] mt-4 font-body">Last updated: February 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="space-y-12">
          {/* Section 1 */}
          <section className="bg-[#141414] rounded-2xl p-8 border border-[#D4AF37]/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center border border-[#D4AF37]/30">
                <Database className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <h2 className="text-2xl font-bold text-white font-heading">Information We Collect</h2>
            </div>
            <div className="text-[#888888] font-body space-y-4">
              <p>We collect information you provide directly to us, including:</p>
              <ul className="list-none space-y-3">
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span><strong className="text-white">Account Details:</strong> Name, email, password, and contact information when you register</span>
                </li>
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span><strong className="text-white">Order Information:</strong> Shipping address, billing details, and payment information</span>
                </li>
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span><strong className="text-white">Communications:</strong> Messages, inquiries, and support requests</span>
                </li>
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span><strong className="text-white">Newsletter:</strong> Email address when you subscribe to our exclusive list</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 2 */}
          <section className="bg-[#141414] rounded-2xl p-8 border border-[#D4AF37]/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center border border-[#D4AF37]/30">
                <Eye className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <h2 className="text-2xl font-bold text-white font-heading">How We Use Your Information</h2>
            </div>
            <div className="text-[#888888] font-body space-y-4">
              <p>We use the information we collect to:</p>
              <ul className="list-none space-y-3">
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>Process and fulfill your orders with premium care</span>
                </li>
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>Send order confirmations and delivery updates</span>
                </li>
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>Provide exceptional customer support</span>
                </li>
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>Share exclusive offers and new arrivals (with your consent)</span>
                </li>
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>Enhance our services and your shopping experience</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section className="bg-[#141414] rounded-2xl p-8 border border-[#D4AF37]/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center border border-[#D4AF37]/30">
                <Lock className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <h2 className="text-2xl font-bold text-white font-heading">Data Security</h2>
            </div>
            <div className="text-[#888888] font-body space-y-4">
              <p>We implement industry-leading security measures to protect your information:</p>
              <ul className="list-none space-y-3">
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>256-bit SSL encryption for all data transmission</span>
                </li>
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>Secure payment processing via Stripe</span>
                </li>
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>Restricted access to personal data</span>
                </li>
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>Regular security assessments and updates</span>
                </li>
              </ul>
              <p className="mt-4">
                We never store your complete payment details. All transactions are processed securely through our certified payment partners.
              </p>
            </div>
          </section>

          {/* Section 4 */}
          <section className="bg-[#141414] rounded-2xl p-8 border border-[#D4AF37]/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center border border-[#D4AF37]/30">
                <FileText className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <h2 className="text-2xl font-bold text-white font-heading">Your Rights</h2>
            </div>
            <div className="text-[#888888] font-body space-y-4">
              <p>You have complete control over your personal data:</p>
              <ul className="list-none space-y-3">
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>Access and review your personal information</span>
                </li>
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>Update or correct any inaccuracies</span>
                </li>
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>Request deletion of your data</span>
                </li>
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>Opt-out of marketing communications</span>
                </li>
                <li className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>Export your data in a portable format</span>
                </li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us at <a href={`mailto:${settings?.legal_email || settings?.email || 'privacy@boxverra.com'}`} className="text-[#D4AF37] hover:underline">{settings?.legal_email || settings?.email || 'privacy@boxverra.com'}</a>
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section className="bg-gradient-to-r from-[#D4AF37] via-[#F4E4BC] to-[#D4AF37] rounded-2xl p-8 text-[#0A0A0A]">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#0A0A0A] rounded-lg flex items-center justify-center">
                <Mail className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <h2 className="text-2xl font-bold font-heading">Contact Us</h2>
            </div>
            <div className="font-body">
              <p className="mb-4">
                If you have any questions about this Privacy Policy, please reach out:
              </p>
              <div className="bg-[#0A0A0A] rounded-xl p-6 text-white">
                <p className="mb-2 text-[#D4AF37] font-bold text-lg">{settings?.business_name || 'BOXVERRA'}</p>
                <p className="mb-1 text-[#888888]">Email: <a href={`mailto:${settings?.legal_email || settings?.email || 'privacy@boxverra.com'}`} className="text-[#D4AF37] hover:underline">{settings?.legal_email || settings?.email || 'privacy@boxverra.com'}</a></p>
                <p className="text-[#888888]">{settings?.address ? `${settings.address.city}, ${settings.address.state} ${settings.address.zip}` : 'Beverly Hills, CA 90210'}</p>
              </div>
            </div>
          </section>
        </div>

        {/* Back Link */}
        <div className="mt-12 pt-8 border-t border-[#D4AF37]/20 text-center">
          <Link to="/" className="text-[#D4AF37] hover:underline font-body">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
